import React, { useEffect, useMemo, useState } from 'react';
import { fetchJourneyFilters } from '../../api/journeys';
import { fetchScanHeatmap } from '../../api/phase2Analytics';
import { PanelStateMessage } from '../../components/dashboard/PanelStateMessage';
import { ConfidenceStrip } from '../../components/journeys/ConfidenceStrip';
import { JourneyFilterBar } from '../../components/journeys/JourneyFilterBar';
import { ScanHeatmapBoard } from '../../components/journeys/ScanHeatmapBoard';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/Card';
import { useAuth } from '../../context/AuthContext';
import { useTenantView } from '../../context/TenantViewContext';
import { JOURNEY_FILTER_OPTIONS } from '../../data/journeysMock';
import { usePersistedState } from '../../hooks/usePersistedState';
import { formatNumber } from '../../lib/formatMoney';
import { buildJourneyApiFilters } from '../../lib/journeyApiFilters';
import { mapScanHeatmapToView } from '../../lib/mapPhase2Analytics';

const DEFAULT_FILTERS = {
  season: 'all',
  venue: 'all',
  event: 'all',
};

const PREFS_KEY = 'vi.scanHeatmap.prefs.v1';
const ALL_VENUE = { value: 'all', label: 'Venue: all' };
const ALL_EVENT = { value: 'all', label: 'Event: all' };

function mapFilterOptions(items, kind) {
  return (items || []).map((item) => ({
    value: String(item.value),
    label: kind === 'venue' ? `Venue: ${item.label}` : `Event: ${item.label}`,
  }));
}

export function ScanHeatmapPage() {
  const { isSuperAdmin } = useAuth();
  const { viewingTenant } = useTenantView();
  const accountId = isSuperAdmin && viewingTenant?.id ? viewingTenant.id : null;

  const [storedFilters, setStoredFilters] = usePersistedState(PREFS_KEY, DEFAULT_FILTERS);
  const appliedFilters = { ...DEFAULT_FILTERS, ...storedFilters };
  const [draftFilters, setDraftFilters] = useState(() => ({
    ...DEFAULT_FILTERS,
    ...storedFilters,
  }));
  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [venueOptions, setVenueOptions] = useState([ALL_VENUE, ...JOURNEY_FILTER_OPTIONS.venues.slice(1)]);
  const [eventOptions, setEventOptions] = useState([ALL_EVENT, ...JOURNEY_FILTER_OPTIONS.events.slice(1)]);

  const filterFields = useMemo(
    () => [
      {
        key: 'season',
        label: 'Season',
        options: [
          { value: 'all', label: 'All seeded dates' },
          ...JOURNEY_FILTER_OPTIONS.seasons,
        ],
      },
      { key: 'venue', label: 'Venue', options: venueOptions },
      { key: 'event', label: 'Event', options: eventOptions },
    ],
    [venueOptions, eventOptions]
  );

  function handleFilterChange(key, value) {
    setDraftFilters((prev) => ({ ...prev, [key]: value }));
  }

  function handleApply() {
    setStoredFilters(draftFilters);
  }

  useEffect(() => {
    let cancelled = false;
    async function loadFilters() {
      try {
        const data = await fetchJourneyFilters({ accountId });
        if (cancelled) return;
        setVenueOptions([ALL_VENUE, ...mapFilterOptions(data.venues, 'venue')]);
        setEventOptions([ALL_EVENT, ...mapFilterOptions(data.events, 'event')]);
      } catch {
        // Keep mock options.
      }
    }
    loadFilters();
    return () => {
      cancelled = true;
    };
  }, [accountId]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const filters = buildJourneyApiFilters({
          season: appliedFilters.season,
          venue: appliedFilters.venue,
          event: appliedFilters.event,
          accountId,
        });
        const { data, source } = await fetchScanHeatmap(filters);
        if (cancelled) return;
        setView(mapScanHeatmapToView(data, source));
      } catch (err) {
        if (cancelled) return;
        setError(err?.message || 'Unable to load scan heatmap.');
        setView(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [appliedFilters.season, appliedFilters.venue, appliedFilters.event, accountId]);

  return (
    <section className="flex w-full min-w-0 flex-col gap-4">
      <JourneyFilterBar
        fields={filterFields}
        values={draftFilters}
        onChange={handleFilterChange}
        onApply={handleApply}
        dataSourceLabel={view?.dataSourceLabel}
      />

      {view?.confidence?.title ? (
        <ConfidenceStrip tone={view.confidence.tone} title={view.confidence.title} />
      ) : null}

      {loading ? (
        <PanelStateMessage
          title="Loading scan heatmap"
          message="Aggregating ACP scans by gate and hour of day."
        />
      ) : error ? (
        <PanelStateMessage title="Couldn't load scan heatmap" message={error} variant="error" />
      ) : !view || view.empty ? (
        <PanelStateMessage
          title="No ACP scan data"
          message="Gate/zone heatmaps stay empty until ACP events land in vi_events with gate payload."
        />
      ) : (
        <Card>
          <CardHeader className="px-4 py-3">
            <CardTitle>Gate × hour</CardTitle>
            <CardDescription>
              {formatNumber(view.totalScans)} scans across {view.gates.length} gates — ingress by
              gate
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ScanHeatmapBoard
              hours={view.hours}
              gates={view.gates}
              maxCell={view.maxCell}
            />
          </CardContent>
        </Card>
      )}
    </section>
  );
}
